import { isValidPhone, PHONE_DIGITS } from "./phone";

export const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

export const isValidUrl = (v) => {
  const s = String(v || "").trim();
  if (!s) return false;
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};

const isEmpty = (v) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && v.trim() === "") ||
  (v instanceof FileList && v.length === 0);

const getField = (form, name) => {
  if (!form || !name) return null;
  const el = form.elements.namedItem(name);
  if (!el || el instanceof RadioNodeList) return null;
  return el;
};

const markInvalid = (form, name, message) => {
  const el = getField(form, name);
  if (!el) return;
  el.setCustomValidity(message || "Invalid value");
  el.classList.add("is-invalid");
};

/**
 * Validate form fields and mark failures with a red border (no toasts).
 *
 * Each rule: { value, name, label, required?, type?, minLength?, custom? }
 *   - name: matches the input/select/textarea `name` attribute
 *   - required: must not be empty
 *   - type: "email" | "url" (only checked when a value is present)
 *   - minLength: minimum length (only checked when a value is present)
 *   - custom: (value) => errorMessage | falsy
 */
export const validateForm = (form, rules = []) => {
  if (!(form instanceof HTMLFormElement)) return false;

  form.classList.add("was-validated");

  const ruledNames = new Set(rules.map((r) => r.name).filter(Boolean));
  for (const name of ruledNames) {
    const el = getField(form, name);
    if (el) {
      el.setCustomValidity("");
      el.classList.remove("is-invalid");
    }
  }

  let valid = true;

  for (const rule of rules) {
    const { value, name, label, required, type, minLength, custom } = rule;
    let msg = null;

    if (required && isEmpty(value)) {
      msg = `${label} is required`;
    }

    if (!msg && typeof custom === "function") {
      const customMsg = custom(value);
      if (customMsg) msg = customMsg;
    }

    if (!msg && !isEmpty(value)) {
      if (type === "email" && !isValidEmail(value)) {
        msg = `Please enter a valid ${label.toLowerCase()}`;
      } else if (type === "auMobile" && !isValidPhone(value, { required: !!required })) {
        msg = required
          ? `Enter ${label.toLowerCase()} (${PHONE_DIGITS} digits)`
          : `Enter a valid ${label.toLowerCase()} (${PHONE_DIGITS} digits) or leave blank`;
      } else if (type === "url" && !isValidUrl(value)) {
        msg = `Please enter a valid ${label.toLowerCase()} (https://…)`;
      } else if (minLength && String(value).trim().length < minLength) {
        msg = `${label} must be at least ${minLength} characters`;
      }
    }

    if (msg) {
      if (name) markInvalid(form, name, msg);
      valid = false;
    }
  }

  form.querySelectorAll("input, select, textarea").forEach((el) => {
    if (!el.willValidate) return;
    // Fields validated by rules above — don't let HTML pattern override them.
    if (ruledNames.has(el.name)) {
      if (el.classList.contains("is-invalid")) valid = false;
      return;
    }
    if (!el.checkValidity()) {
      el.classList.add("is-invalid");
      valid = false;
    } else {
      el.classList.remove("is-invalid");
    }
  });

  if (!valid) {
    const first = form.querySelector(".is-invalid, :invalid");
    first?.focus?.();
  }

  return valid;
};

export const validateRequiredForm = (form) => {
  if (!(form instanceof HTMLFormElement)) return false;

  form.classList.add("was-validated");

  let valid = true;
  form.querySelectorAll("input, select, textarea").forEach((el) => {
    if (!el.willValidate) return;
    if (!el.validity.customError) el.setCustomValidity("");
    if (!el.checkValidity()) {
      el.classList.add("is-invalid");
      valid = false;
    } else {
      el.classList.remove("is-invalid");
    }
  });

  if (!valid) {
    form.querySelector(".is-invalid, :invalid")?.focus?.();
  }

  return valid;
};

export default validateForm;
